/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useTranslation } from 'react-i18next';

import { borrowed, type ResolvedField } from '@/lib/profileField';
import { SelectField, TextField } from './ProfileFields';
import type { AddressKey, DealershipAddress } from './useDealershipAddress';

/**
 * The six address fields, read-only wherever GDL answered and asked for wherever it did not.
 *
 * `readOnlyWhereKnown` widens that to anything already filled in, for a dialog that only borrows
 * the address: the dealership is settled in the profile, so showing it there is a reminder of what
 * is being sent, not an invitation to fork it. Blanks stay editable, since that dialog is the last
 * chance to supply what the profile never managed to.
 */
export function DealershipAddressFields({
  address,
  readOnlyWhereKnown,
}: {
  address: DealershipAddress;
  readOnlyWhereKnown?: boolean;
}) {
  const { t } = useTranslation();
  const { change } = address;
  const field = (key: AddressKey): ResolvedField =>
    readOnlyWhereKnown ? borrowed(address.fields[key]) : address.fields[key];
  return (
    <div className="pf-grid">
      <TextField
        label={t('profile.labels.dealer')}
        field={field('dealerName')}
        onChange={change('dealerName')}
        required
      />
      <TextField
        label={t('profile.streetAddress')}
        field={field('address')}
        onChange={change('address')}
        required
      />
      <SelectField
        label={t('profile.country')}
        field={field('country')}
        options={address.countryOptions}
        onChange={address.changeCountry}
        placeholder={t('profile.selectCountry')}
        required
      />
      <SelectField
        label={t('profile.state')}
        field={field('state')}
        options={address.subdivisions}
        onChange={change('state')}
        placeholder={
          address.selectedCountry ? t('profile.selectState') : t('profile.selectCountryFirst')
        }
        required={address.stateApplies}
        disabled={!address.selectedCountry}
      />
      <TextField
        label={t('profile.city')}
        field={field('city')}
        onChange={change('city')}
        required
      />
      <TextField
        label={t('profile.zipCode')}
        field={field('postalCode')}
        onChange={change('postalCode')}
        required
      />
    </div>
  );
}
